import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { STEPS, FORWARD } from '../../components/u99/trace/traceSteps';
import TraceStep from '../../components/u99/trace/TraceStep';
import TraceResult from '../../components/u99/trace/TraceResult';
import { Label } from '../../components/u99/Label';

const empty = () => Object.fromEntries(STEPS.map((s) => [s.key, '']));

export default function TraceTool() {
  const [phase, setPhase] = useState('intro');
  const [i, setI] = useState(0);
  const [answers, setAnswers] = useState(empty);
  const [intensity, setIntensity] = useState(5);

  useEffect(() => { document.title = 'Trace a Reaction — Psychology Tools'; }, []);
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [phase, i]);

  const restart = () => { setAnswers(empty()); setIntensity(5); setI(0); setPhase('intro'); };
  const step = STEPS[i];

  return (
    <main className="max-w-3xl mx-auto px-5 lg:px-10 pt-28 lg:pt-40 pb-28" data-testid="trace-tool-page">
      <div className="flex items-center justify-between">
        <Link to="/u99/tools" className="inline-flex items-center gap-2 font-mono-precision text-[10px] uppercase tracking-[0.3em] text-slate-500 hover:text-cyan-300 transition-colors" data-testid="trace-back-to-tools"><ArrowLeft size={12} /> Tools</Link>
        <Label id="psychological-guidance" size="xs" />
      </div>

      {phase === 'steps' && (
        <div className="mt-8 flex gap-1.5" data-testid="trace-progress">
          {STEPS.map((s, k) => <span key={s.key} className={`h-px flex-1 transition-colors duration-500 ${k <= i ? 'bg-cyan-400' : 'bg-white/10'}`} />)}
        </div>
      )}

      <div className="mt-10">
        <AnimatePresence mode="wait">
          {phase === 'intro' && (
            <motion.div key="intro" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.6 }} data-testid="trace-intro">
              <p className="font-mono-precision text-[10px] uppercase tracking-[0.3em] text-cyan-300">Tool 01 · Trace a reaction</p>
              <h1 className="mt-5 font-celestial text-4xl sm:text-5xl lg:text-6xl font-light text-slate-100 leading-[1.02]">Follow one reaction <span className="text-cyan-300">back to where it was built</span></h1>
              <p className="mt-7 text-base md:text-lg text-slate-400 leading-relaxed">Think of something you did, said or felt recently that surprised you, or that you keep doing. Seven short questions walk it backward along the chain. At the end you see the whole sequence laid out, and you choose where something new could enter.</p>
              <div className="mt-8 flex flex-wrap items-center gap-1.5" data-testid="trace-chain-preview">
                {[...FORWARD].reverse().map((k, n) => (
                  <span key={k} className="flex items-center gap-1.5">
                    {n > 0 && <span className="text-cyan-400/50 font-mono-precision text-xs">←</span>}
                    <span className="font-mono-precision text-[10px] uppercase tracking-[0.2em] text-slate-300">{STEPS.find((s) => s.key === k).stage}</span>
                  </span>
                ))}
              </div>
              <ul className="mt-8 space-y-2 text-sm text-slate-500">
                <li>· Takes about four minutes.</li>
                <li>· Nothing you type leaves your device.</li>
                <li>· It does not diagnose. It shows you a sequence.</li>
              </ul>
              <button onClick={() => setPhase('steps')} className="mt-10 inline-flex items-center gap-3 font-mono-precision text-[11px] uppercase tracking-[0.25em] px-7 py-4 rounded-full bg-cyan-400 hover:bg-cyan-300 text-[#02040a] transition-colors" data-testid="trace-start-btn">Begin the trace <ArrowRight size={14} /></button>
            </motion.div>
          )}

          {phase === 'steps' && (
            <TraceStep
              key={step.key}
              step={step}
              index={i}
              total={STEPS.length}
              value={answers[step.key]}
              intensity={intensity}
              onChange={(v) => setAnswers((a) => ({ ...a, [step.key]: v }))}
              onIntensity={setIntensity}
              onBack={() => (i === 0 ? setPhase('intro') : setI(i - 1))}
              onNext={() => (i === STEPS.length - 1 ? setPhase('result') : setI(i + 1))}
            />
          )}

          {phase === 'result' && <TraceResult key="result" answers={answers} intensity={intensity} onRestart={restart} />}
        </AnimatePresence>
      </div>
    </main>
  );
}
